import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller } from "react-hook-form";
import type { Control } from "react-hook-form";
import type { ReactNode } from "react";
import { dateUtil } from "@sdeapps/react-core";

interface ControlledDateTimeProps {
  name: string;
  control: Control;
  label: string;
  defaultValue?: string | Date | null;
  readOnly?: boolean;
  rules?: object; // difficile de retranscrire ce type
  maxDate?: Date;
  defaultHelperText?: string;
  variant?: "outlined" | "standard" | "filled";
  fullWidth?: boolean;
}

/**
 * Un DatePicker de mui controllé par react-hook-forms. Permet à
 * l'utilisateur de sélectionner une date.
 * @param name obligatoire - Nom unique de l'input
 * @param control obligatoire - objet Control du formulaire react-hook-forms
 * https://react-hook-form.com/api/useform/control
 * @param label obligatoire - label de l'input
 * @param defaultValue valeur par défaut de l'input
 * @param readOnly booléen déterminant si l'input en lecture seule
 * @param rules règles de validation de l'input au même format que
 * les options de la méthode register() de react-hook-forms
 * https://react-hook-form.com/api/useform/register#options
 * @param maxDate - date maximum de l'input
 * @param defaultHelperText - texte d'aide à afficher lorsque le champ n'est
 * pas en erreur
 * @param variant - variante graphique de l'input "outlined" | "standard" |
 * "filled", par défaut "outlined"
 * @param fullWidth - booléen déterminant si l'input prend toute la place
 * horizontale disponible
 * @returns Un DatePicker de mui controllé par react-hook-forms
 */
function ControlledDateTime({
  name,
  control,
  label,
  defaultValue = "",
  readOnly = false,
  rules,
  maxDate,
  defaultHelperText,
  variant = "outlined",
  fullWidth = true,
}: Readonly<ControlledDateTimeProps>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={dateUtil.getDate(defaultValue)}
      rules={rules}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <DatePicker
          value={value ?? ""}
          onChange={onChange}
          maxDate={maxDate}
          inputRef={ref}
          label={label}
          readOnly={readOnly}
          slotProps={{
            textField: {
              variant,
              fullWidth,
              error: error != null,
              helperText: error?.message ?? defaultHelperText,
            },
          }}
        />
      )}
    />
  );
}

export default ControlledDateTime;
